// src/pages/league-dashboard/salary-contract/tabs/PricingLogicTab.js
// REDESIGNED: 2025-11-27 per PRICING_UI_REDESIGN_SPEC.md
import React, { useState, useEffect } from 'react';
import {
  Settings, RotateCcw, TrendingUp, AlertCircle,
  Zap, DollarSign, BarChart3, Copy, Trash2, Plus,
  ChevronDown, ChevronUp, HelpCircle, Info, Users, Target
} from 'lucide-react';
import { dynastyTheme } from '../../../../services/colorService';
import { DynastyTable } from '../../../../services/tables/DynastyTable';
import { leaguesAPI } from '../../../../services/apiService';
import { parseFloatOrPrev } from '../../../../services/formUtils';
import PositionScarcityModal from '../components/PositionScarcityModal';
import { isRawPitcherPosition } from '../../../../utils/playerClassification';

// =============================================================================
// DEFAULT PRICING LOGIC SETTINGS
// Updated to match spec - removed unused z_score_thresholds
// Exported per Bible §14 frontend testing convention so unit tests can
// import the canonical defaults; also consumed by extracted
// PositionScarcityModal child for the reset-to-defaults action.
// =============================================================================
export const DEFAULT_PRICING_LOGIC = {
  statistical_thresholds: {
    minimum_abs: 200,
    minimum_games_started: 15,
    minimum_relief: 40
  },
  price_constraints: {
    draft_cap_usage_target: 0.75,
    min_value_players_percent: 0.15,
    max_players_to_price_multiplier: 1.5,
    below_median_curve_power: 2.0,  // 1=Linear, 2=Steep, 3=Very Steep (below median)
    above_median_curve_power: 1.0,  // 1=Linear, 1.5=Moderate, 2=Steep (above median — elites vs mid-tier)
    max_price_percent: 0.10         // Max player price as % of per-team target (0.10=10%, 0.15=15%)
  },
  dampening_power: 0.75,           // Impact dampening: 1.0=none, 0.75=moderate, 0.5=sqrt
  position_scarcity: {
    C: 1.15,
    '1B': 1.0,
    '2B': 1.05,
    '3B': 1.0,
    SS: 1.05,
    OF: 1.0,
    CP: 1.20,
    SP: 1.0,
    RP: 1.0
  },
  prior_year_discount: 0.85,
  two_year_discount: 0.70
};

// =============================================================================
// PRESET PROFILES - Updated to remove z_score_thresholds
// Exported per Bible §14 frontend testing convention so unit tests can
// verify the canonical preset list shape.
// =============================================================================
export const PRESET_PROFILES = {
  balanced: {
    name: 'Balanced',
    description: 'Standard distribution with moderate star premiums',
    settings: DEFAULT_PRICING_LOGIC
  },
  topHeavy: {
    name: 'Top-Heavy',
    description: 'More money concentrated in elite players',
    settings: {
      ...DEFAULT_PRICING_LOGIC,
      dampening_power: 0.9,
      price_constraints: {
        ...DEFAULT_PRICING_LOGIC.price_constraints,
        draft_cap_usage_target: 0.80,
        min_value_players_percent: 0.20,
        above_median_curve_power: 1.5,
        max_price_percent: 0.15
      }
    }
  },
  deep: {
    name: 'Deep League',
    description: 'More balanced distribution for deeper rosters',
    settings: {
      ...DEFAULT_PRICING_LOGIC,
      dampening_power: 0.6,
      price_constraints: {
        ...DEFAULT_PRICING_LOGIC.price_constraints,
        min_value_players_percent: 0.10,
        max_players_to_price_multiplier: 1.75,
        max_price_percent: 0.08
      }
    }
  },
  aggressive: {
    name: 'Steep Drop-Off',
    description: 'Full z-score range, big gap between elites and average',
    settings: {
      ...DEFAULT_PRICING_LOGIC,
      dampening_power: 1.0,
      price_constraints: {
        ...DEFAULT_PRICING_LOGIC.price_constraints,
        draft_cap_usage_target: 0.85,
        above_median_curve_power: 2.0,
        max_price_percent: 0.15
      },
      position_scarcity: {
        ...DEFAULT_PRICING_LOGIC.position_scarcity,
        C: 1.25,
        CP: 1.30
      }
    }
  }
};

// =============================================================================
// HELP TEXT COMPONENT
// =============================================================================
const HelpText = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-md mt-3`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-3 ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} transition-colors`}
      >
        <span className="flex items-center text-sm">
          <HelpCircle className="w-4 h-4 mr-2" />
          {title}
        </span>
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isExpanded && (
        <div className={`px-3 pb-3 ${dynastyTheme.classes.text.neutralLight} text-sm`}>
          {children}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const PricingLogicTab = ({
  settings,
  setSettings,
  pricingData,
  dataLoaded,
  league,
  leagueId
}) => {
  const [pricingLogic, setPricingLogic] = useState(DEFAULT_PRICING_LOGIC);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [pricingLogicLoaded, setPricingLogicLoaded] = useState(false); // Track if DB loaded
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [leagueStats, setLeagueStats] = useState(null);
  const [activePreset, setActivePreset] = useState('balanced');

  // Modal states
  const [positionScarcityModalOpen, setPositionScarcityModalOpen] = useState(false);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);

  // Scenario management
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState('');
  const [savingScenario, setSavingScenario] = useState(false);

  // Draft cap from settings
  const draftCap = settings?.draft_cap ?? settings?.salary_cap ?? 600;

  // Load existing pricing logic settings
  useEffect(() => {
    if (leagueId) {
      loadPricingLogicSettings();
      loadSavedScenarios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  // Calculate league statistics when pricing data loads or thresholds change
  useEffect(() => {
    if (pricingData && dataLoaded) {
      calculateLeagueStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingData, dataLoaded, pricingLogic.statistical_thresholds]);

  // Track changes
  useEffect(() => {
    if (originalSettings) {
      setHasChanges(JSON.stringify(pricingLogic) !== JSON.stringify(originalSettings));
    }
  }, [pricingLogic, originalSettings]);

  // SYNC: pricingLogic.draft_cap_usage_target → settings.draft_cap_usage
  // DB is the source of truth. Keep PricingTab in sync with PricingLogicTab.
  // Only syncs AFTER DB has loaded to avoid overwriting DB value with defaults.
  useEffect(() => {
    if (pricingLogicLoaded && setSettings) {
      const currentValue = pricingLogic.price_constraints.draft_cap_usage_target;
      if (currentValue !== undefined) {
        setSettings(prev => {
          // Only update if different to avoid unnecessary re-renders
          if (prev.draft_cap_usage !== currentValue) {
            return { ...prev, draft_cap_usage: currentValue };
          }
          return prev;
        });
      }
    }
  }, [pricingLogicLoaded, pricingLogic.price_constraints.draft_cap_usage_target, setSettings]);

  // =============================================================================
  // DATA LOADING FUNCTIONS
  // =============================================================================
  const loadPricingLogicSettings = async () => {
    try {
      const response = await leaguesAPI.getPricingLogicSettings(leagueId);
      if (response.success && response.settings) {
        // Merge with defaults to ensure all keys exist
        // DB is the source of truth for draft_cap_usage_target
        const mergedSettings = {
          ...DEFAULT_PRICING_LOGIC,
          ...response.settings,
          statistical_thresholds: {
            ...DEFAULT_PRICING_LOGIC.statistical_thresholds,
            ...(response.settings.statistical_thresholds ?? {})
          },
          price_constraints: {
            ...DEFAULT_PRICING_LOGIC.price_constraints,
            ...(response.settings.price_constraints ?? {})
          },
          position_scarcity: {
            ...DEFAULT_PRICING_LOGIC.position_scarcity,
            ...(response.settings.position_scarcity ?? {})
          }
        };
        setPricingLogic(mergedSettings);
        setOriginalSettings(mergedSettings);
      } else {
        setOriginalSettings(DEFAULT_PRICING_LOGIC);
      }
      setPricingLogicLoaded(true); // Mark DB as loaded
    } catch (error) {
      console.error('Error loading pricing logic settings:', error);
      setOriginalSettings(DEFAULT_PRICING_LOGIC);
      setPricingLogicLoaded(true); // Mark as loaded even on error
    }
  };

  const loadSavedScenarios = async () => {
    try {
      const response = await leaguesAPI.getPricingScenarios(leagueId);
      if (response.success) {
        setSavedScenarios(response.scenarios ?? []);
      }
    } catch (error) {
      console.error('Error loading scenarios:', error);
    }
  };

  const calculateLeagueStats = () => {
    if (!pricingData || !pricingData.players) return;

    const minABs = pricingLogic.statistical_thresholds.minimum_abs;
    const minGS = pricingLogic.statistical_thresholds.minimum_games_started;
    const minRelief = pricingLogic.statistical_thresholds.minimum_relief;

    // Get the pricing year from metadata (backend determines which year to use)
    const pricingYear = pricingData.metadata?.pricing_year;

    // Helper to get stats for pricing year from years array
    const getStatsForYear = (player, year) => {
      const years = player.stats?.years ?? [];
      // Sort years descending and find the pricing year, or fall back to most recent
      const sortedYears = [...years].sort((a, b) => (b.season ?? 0) - (a.season ?? 0));

      if (year) {
        const yearData = sortedYears.find(y => y.season === year);
        if (yearData) return yearData;
      }
      // Fallback to most recent year if pricing year not found
      return sortedYears[0] ?? {};
    };

    // Count ALL hitters and pitchers
    const allHitters = pricingData.players.filter(p => {
      // String `||` (not `??`): empty position string treated as missing for filter.
      const pos = p.info?.position || '';
      return !isRawPitcherPosition(pos.toUpperCase());
    });

    const allPitchers = pricingData.players.filter(p => {
      // String `||` (not `??`): empty position string treated as missing for filter.
      const pos = p.info?.position || '';
      return isRawPitcherPosition(pos.toUpperCase());
    });

    // Filter for qualified players based on thresholds using pricing_year stats
    const qualifiedHitters = allHitters.filter(p => {
      const yearData = getStatsForYear(p, pricingYear);
      const hitting = yearData.hitting ?? {};
      // 8m.6d-pricing-logic: collapsed vestigial `|| hitting.ab` alias chain.
      // Backend canonical (routers/leagues/players/models.py:16/77/192/235/269)
      // always emits `at_bats`; `ab` was a never-resolved fallback alias.
      const ab = hitting.at_bats ?? 0;
      return ab >= minABs;
    });

    const qualifiedStarters = allPitchers.filter(p => {
      const yearData = getStatsForYear(p, pricingYear);
      const pitching = yearData.pitching ?? {};
      // 8m.6d-pricing-logic: collapsed vestigial `|| pitching.gs` alias chain.
      // Backend canonical (models.py:45/99/250) always emits `games_started`.
      const gs = pitching.games_started ?? 0;
      return gs >= minGS;
    });

    const qualifiedRelievers = allPitchers.filter(p => {
      const yearData = getStatsForYear(p, pricingYear);
      const pitching = yearData.pitching ?? {};
      // 8m.6d-pricing-logic: collapsed vestigial `|| pitching.ip` and
      // `|| pitching.gs` alias chains. Backend canonical always emits
      // `innings_pitched` and `games_started`.
      const ip = pitching.innings_pitched ?? 0;
      const gs = pitching.games_started ?? 0;
      return ip >= minRelief && gs < minGS; // Relief pitchers with enough IP but not starters
    });

    // Calculate anchor players count
    const numTeams = league?.league_settings?.max_teams ?? 12;
    const rosterSize = league?.roster_settings?.max_players_total ?? 23;
    const multiplier = pricingLogic.price_constraints.max_players_to_price_multiplier ?? 1.5;
    const anchorPlayerCount = Math.round(numTeams * rosterSize * multiplier);
    const anchorPercent = ((anchorPlayerCount / pricingData.players.length) * 100).toFixed(1);

    setLeagueStats({
      totalPlayers: pricingData.players.length,
      hitting: allHitters.length,
      pitching: allPitchers.length,
      qualifiedHitters: qualifiedHitters.length,
      qualifiedStarters: qualifiedStarters.length,
      qualifiedRelievers: qualifiedRelievers.length,
      qualifiedPitchers: qualifiedStarters.length + qualifiedRelievers.length,
      anchorPlayerCount,
      anchorPercent,
      numTeams,
      rosterSize,
      multiplier
    });
  };

  // =============================================================================
  // CALCULATED VALUES
  // =============================================================================
  const getCalculatedValues = () => {
    const usageTarget = pricingLogic.price_constraints.draft_cap_usage_target ?? 0.75;
    const minValuePercent = pricingLogic.price_constraints.min_value_players_percent ?? 0.15;

    const perTeamTarget = Math.round(draftCap * usageTarget);
    const maxPlayerPrice = Math.round(perTeamTarget * 0.10); // 10% of per-team target
    const minSalary = settings?.min_salary ?? 2;
    const minValuePlayersCount = leagueStats ? Math.round(leagueStats.totalPlayers * minValuePercent) : 0;

    return {
      usageTarget,
      perTeamTarget,
      maxPlayerPrice,
      minSalary,
      minValuePercent,
      minValuePlayersCount
    };
  };

  const calculatedValues = getCalculatedValues();

  // =============================================================================
  // SAVE FUNCTIONS
  // =============================================================================
  const handleSave = async () => {
    // 8m.10b defensive null-validation gates.
    // Per 8m.10a precedent: null reach is unreachable via normal UI interaction
    // (Slider helper uses parseFloatOrPrev which preserves prev on clear; load
    // handlers use ?? canonical converting null/undefined to canonical
    // defaults). Gates protect against future code paths, direct programmatic
    // state manipulation, and backend response shape changes. Fires before API
    // call to avoid sending invalid payload.
    if (pricingLogic.price_constraints?.draft_cap_usage_target === null ||
        pricingLogic.price_constraints?.draft_cap_usage_target === undefined) {
      alert('Draft cap usage target is required');
      return;
    }
    if (pricingLogic.price_constraints?.min_value_players_percent === null ||
        pricingLogic.price_constraints?.min_value_players_percent === undefined) {
      alert('Min value players percent is required');
      return;
    }
    if (pricingLogic.price_constraints?.max_players_to_price_multiplier === null ||
        pricingLogic.price_constraints?.max_players_to_price_multiplier === undefined) {
      alert('Max players to price multiplier is required');
      return;
    }
    if (pricingLogic.price_constraints?.below_median_curve_power === null ||
        pricingLogic.price_constraints?.below_median_curve_power === undefined) {
      alert('Below median curve power is required');
      return;
    }

    try {
      setSaving(true);

      const response = await leaguesAPI.savePricingLogicSettings(leagueId, pricingLogic);

      if (response.success) {
        setOriginalSettings(pricingLogic);
        setHasChanges(false);

        // Sync draft_cap_usage_target back to parent settings state
        // This keeps both tabs in sync when saving from this tab
        if (setSettings && pricingLogic.price_constraints.draft_cap_usage_target !== undefined) {
          setSettings(prev => ({
            ...prev,
            draft_cap_usage: pricingLogic.price_constraints.draft_cap_usage_target
          }));
        }

        alert('Pricing logic settings saved successfully!');
      } else {
        // String `||` (not `??`): empty error-message string treated as missing.
        alert('Failed to save settings: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving pricing logic:', error);
      alert('Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset to original settings? Unsaved changes will be lost.')) {
      setPricingLogic(originalSettings ?? DEFAULT_PRICING_LOGIC);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Reset to default settings? This will replace all current values.')) {
      setPricingLogic(DEFAULT_PRICING_LOGIC);
    }
  };

  // =============================================================================
  // SCENARIO MANAGEMENT
  // =============================================================================
  const handleSaveScenario = async () => {
    if (!scenarioName.trim()) {
      alert('Please enter a scenario name');
      return;
    }

    try {
      setSavingScenario(true);

      const scenario = {
        name: scenarioName,
        settings: pricingLogic,
        created_at: new Date().toISOString()
      };

      const response = await leaguesAPI.savePricingScenario(leagueId, scenario);

      if (response.success) {
        await loadSavedScenarios();
        setScenarioName('');
        alert('Scenario saved successfully!');
      }
    } catch (error) {
      console.error('Error saving scenario:', error);
      alert('Failed to save scenario');
    } finally {
      setSavingScenario(false);
    }
  };

  const handleLoadScenario = (scenario) => {
    if (hasChanges && !window.confirm('Load scenario? Unsaved changes will be lost.')) {
      return;
    }
    setPricingLogic(scenario.settings);
  };

  const handleDeleteScenario = async (scenarioId) => {
    if (!window.confirm('Delete this scenario?')) return;

    try {
      const response = await leaguesAPI.deletePricingScenario(leagueId, scenarioId);
      if (response.success) {
        await loadSavedScenarios();
      }
    } catch (error) {
      console.error('Error deleting scenario:', error);
    }
  };

  // =============================================================================
  // PRESET LOADING
  // =============================================================================
  const handleLoadPreset = (presetKey) => {
    if (hasChanges && !window.confirm('Load preset? Unsaved changes will be lost.')) {
      return;
    }
    setPricingLogic(PRESET_PROFILES[presetKey].settings);
    setActivePreset(presetKey);
  };

  // =============================================================================
  // PREVIEW GENERATION
  // =============================================================================
  const handleGeneratePreview = async () => {
    if (!pricingData || !dataLoaded) {
      alert('Player data not loaded yet. Please wait.');
      return;
    }

    try {
      setGeneratingPreview(true);

      // Call server-side pricing engine with custom pricing logic
      const response = await leaguesAPI.generatePlayerPrices(leagueId, pricingLogic);

      if (response.success && response.prices) {
        const sortedPrices = response.prices.sort((a, b) => {
          const aScore = a.pricing?.impact_score ?? 0;
          const bScore = b.pricing?.impact_score ?? 0;
          return bScore - aScore;
        });

        // Filter by actual tier field from backend (based on salary % of draft cap)
        // Tiers: elite (>=6%), star (>=5%), solid (>=3%), regular (>=1.5%), replacement (<1.5%)
        const elite = sortedPrices.filter(p => p.pricing?.tier === 'elite' || p.pricing?.tier === 'star').slice(0, 5);
        const mid = sortedPrices.filter(p => p.pricing?.tier === 'solid' || p.pricing?.tier === 'regular').slice(0, 5);
        const replacement = sortedPrices.filter(p => p.pricing?.tier === 'replacement').slice(0, 5);

        // Map canonical player structure to flat fields for DynastyTable
        const mapPlayer = (p) => ({
          ...p,
          // String `||` (not `??`) on 3 lines: empty strings treated as missing
          // for preview-row display. team chain handles canonical mlb_team plus
          // legacy team field-name compatibility.
          player_name: p.info?.full_name || 'Unknown',
          position: p.info?.position || 'UTIL',
          team: p.info?.mlb_team || p.info?.team || '-',
          price: p.pricing?.baseline_price ?? 0,
          aggregate_z_score: p.pricing?.impact_score ?? 0
        });

        setPreviewData({
          elite: elite.map(mapPlayer),
          mid: mid.map(mapPlayer),
          replacement: replacement.map(mapPlayer),
          summary: response.summary
        });
      } else {
        // String `||` (not `??`): empty error-message string treated as missing.
        throw new Error(response.message || 'Failed to generate preview on server');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Failed to generate preview: ' + error.message);
    } finally {
      setGeneratingPreview(false);
    }
  };

  // =============================================================================
  // SLIDER COMPONENT
  // =============================================================================
  const Slider = ({ label, value, onChange, min, max, step, description, suffix = '', showWarning, warningText }) => (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <label className={dynastyTheme.components.label}>{label}</label>
        <span className={`${dynastyTheme.classes.text.primary} font-bold`}>
          {value}{suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloatOrPrev(e.target.value, value))}
        className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-yellow-400"
      />
      <div className="flex items-center justify-between mt-1">
        <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>{min}{suffix}</span>
        <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>{max}{suffix}</span>
      </div>
      {description && (
        <p className={`${dynastyTheme.classes.text.neutralLight} text-xs mt-1`}>
          {description}
        </p>
      )}
      {showWarning && (
        <p className={`text-yellow-500 text-xs mt-1`}>
          ⚠️ {warningText}
        </p>
      )}
    </div>
  );

  // =============================================================================
  // RENDER
  // =============================================================================
  return (
    <div className="space-y-6">
      {/* Header with Save/Reset */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className={dynastyTheme.components.heading.h2}>
                <Settings className="inline w-6 h-6 mr-2" />
                Pricing Logic Configuration
              </h2>
              <p className={dynastyTheme.classes.text.neutralLight}>
                Configure how the adaptive salary engine calculates player prices
              </p>
            </div>
            <div className="flex gap-2">
              {hasChanges && (
                <span className={`px-3 py-1 rounded-md text-sm ${dynastyTheme.classes.bg.warning} ${dynastyTheme.classes.text.white}`}>
                  Unsaved Changes
                </span>
              )}
              <button
                onClick={handleResetToDefaults}
                className={dynastyTheme.utils.getComponent('button', 'ghost', 'sm')}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </button>
              <button
                onClick={handleReset}
                disabled={!hasChanges}
                className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
              >
                Undo Changes
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={dynastyTheme.utils.getComponent('button', 'primary', 'sm')}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Presets */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <Zap className="inline w-5 h-5 mr-2" />
            Quick Presets
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            {Object.entries(PRESET_PROFILES).map(([key, profile]) => (
              <button
                key={key}
                onClick={() => handleLoadPreset(key)}
                className={`p-4 rounded-md border-2 ${dynastyTheme.classes.transition} ${
                  activePreset === key
                    ? `${dynastyTheme.classes.border.primary} ${dynastyTheme.classes.bg.primaryLight}`
                    : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                }`}
              >
                <h4 className={dynastyTheme.classes.text.white + ' font-semibold mb-1'}>
                  {profile.name}
                </h4>
                <p className={dynastyTheme.classes.text.neutralLight + ' text-xs'}>
                  {profile.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* SECTION 1: League Statistics (Read-Only) */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <BarChart3 className="inline w-5 h-5 mr-2" />
            League Statistics
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
            Current player population based on your threshold settings
          </p>

          {leagueStats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md text-center`}>
                  <div className={`${dynastyTheme.classes.text.primary} text-3xl font-bold mb-1`}>
                    {leagueStats.totalPlayers}
                  </div>
                  <div className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>Total Players</div>
                </div>
                <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md text-center`}>
                  <div className={`${dynastyTheme.classes.text.primary} text-3xl font-bold mb-1`}>
                    {leagueStats.qualifiedHitters}
                  </div>
                  <div className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>Qualified Hitters</div>
                  <div className={dynastyTheme.classes.text.neutralLight + ' text-xs'}>
                    ({pricingLogic.statistical_thresholds.minimum_abs}+ ABs)
                  </div>
                </div>
                <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md text-center`}>
                  <div className={`${dynastyTheme.classes.text.primary} text-3xl font-bold mb-1`}>
                    {leagueStats.qualifiedPitchers}
                  </div>
                  <div className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>Qualified Pitchers</div>
                  <div className={dynastyTheme.classes.text.neutralLight + ' text-xs'}>
                    ({pricingLogic.statistical_thresholds.minimum_games_started}+ GS / {pricingLogic.statistical_thresholds.minimum_relief}+ IP)
                  </div>
                </div>
                <div className={`${dynastyTheme.classes.bg.primary} p-4 rounded-md text-center border-2 border-yellow-400`}>
                  <div className={`${dynastyTheme.classes.text.dark} text-3xl font-bold mb-1`}>
                    {leagueStats.anchorPlayerCount}
                  </div>
                  <div className={`${dynastyTheme.classes.text.dark} text-sm font-semibold`}>Anchor Players</div>
                  <div className={`${dynastyTheme.classes.text.dark} text-xs mt-1 opacity-75`}>
                    (Top {leagueStats.anchorPercent}% for pricing curve)
                  </div>
                </div>
              </div>

              {leagueStats.qualifiedHitters + leagueStats.qualifiedPitchers < 200 && (
                <div className={`p-3 rounded-md ${dynastyTheme.classes.bg.warning} ${dynastyTheme.classes.text.white} text-sm`}>
                  <AlertCircle className="inline w-4 h-4 mr-2" />
                  Small population ({leagueStats.qualifiedHitters + leagueStats.qualifiedPitchers} qualified players) - z-scores may be unstable. Consider lowering thresholds.
                </div>
              )}
            </>
          ) : (
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} text-center`}>
              <p className={dynastyTheme.classes.text.neutralLight}>
                Loading player statistics...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 2: Population Thresholds */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <TrendingUp className="inline w-5 h-5 mr-2" />
            Population Thresholds
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
            Define minimum stats required to qualify for z-score calculations
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Minimum At-Bats for Full Stats"
                value={pricingLogic.statistical_thresholds.minimum_abs}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  statistical_thresholds: {
                    ...pricingLogic.statistical_thresholds,
                    minimum_abs: Math.round(v)
                  }
                })}
                min={25}
                max={500}
                step={25}
                suffix=" ABs"
                description={leagueStats ? `Current: ~${leagueStats.qualifiedHitters} qualified hitters` : ''}
              />
            </div>

            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Minimum Games Started (SP)"
                value={pricingLogic.statistical_thresholds.minimum_games_started}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  statistical_thresholds: {
                    ...pricingLogic.statistical_thresholds,
                    minimum_games_started: Math.round(v)
                  }
                })}
                min={5}
                max={30}
                step={1}
                suffix=" GS"
                description={leagueStats ? `Current: ~${leagueStats.qualifiedStarters} qualified starters` : ''}
              />
            </div>

            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Minimum Innings Pitched (RP)"
                value={pricingLogic.statistical_thresholds.minimum_relief}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  statistical_thresholds: {
                    ...pricingLogic.statistical_thresholds,
                    minimum_relief: Math.round(v)
                  }
                })}
                min={20}
                max={120}
                step={5}
                suffix=" IP"
                description={leagueStats ? `Current: ~${leagueStats.qualifiedRelievers} qualified relievers` : ''}
              />
            </div>
          </div>

          <HelpText title="What do these thresholds do?">
            <p className="mb-2">
              These thresholds determine which players are included when calculating league-wide mean and standard deviation for z-scores.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="font-semibold text-green-400 mb-1">Lower thresholds:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Larger population (more stable statistics)</li>
                  <li>Includes injured stars (e.g., Acuña with 192 ABs)</li>
                  <li>May include small-sample performances</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-red-400 mb-1">Higher thresholds:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Only "qualified" players (traditional)</li>
                  <li>Smaller population (less stable)</li>
                  <li>Excludes injured stars</li>
                </ul>
              </div>
            </div>
            <p className="mt-3 text-yellow-400">
              Recommendation: 200 ABs / 15 GS / 40 IP for modern MLB
            </p>
          </HelpText>
        </div>
      </div>

      {/* SECTION 2B: Pricing Curve Controls */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <Zap className="inline w-5 h-5 mr-2" />
            Pricing Curve Controls
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
            Control how z-scores translate to dollar values — these have the biggest impact on price spread
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Impact Dampening Power"
                value={pricingLogic.dampening_power ?? 0.75}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  dampening_power: parseFloat(v.toFixed(2))
                })}
                min={0.5}
                max={1.0}
                step={0.05}
                description="1.0 = No dampening (full z-score range). 0.5 = sqrt (compressed)"
              />
            </div>

            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Max Player Price %"
                value={(pricingLogic.price_constraints?.max_price_percent ?? 0.10) * 100}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  price_constraints: {
                    ...pricingLogic.price_constraints,
                    max_price_percent: parseFloat((v / 100).toFixed(2))
                  }
                })}
                min={5}
                max={20}
                step={1}
                suffix="%"
                description="Max player price as % of per-team budget. Higher = more elite separation"
              />
            </div>

            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Above-Median Curve Power"
                value={pricingLogic.price_constraints?.above_median_curve_power ?? 1.0}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  price_constraints: {
                    ...pricingLogic.price_constraints,
                    above_median_curve_power: parseFloat(v.toFixed(1))
                  }
                })}
                min={0.5}
                max={3.0}
                step={0.1}
                description="1.0 = linear. 2.0 = steep (big gap between elites and mid-tier)"
              />
            </div>

            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Below-Median Curve Power"
                value={pricingLogic.price_constraints?.below_median_curve_power ?? 2.0}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  price_constraints: {
                    ...pricingLogic.price_constraints,
                    below_median_curve_power: parseFloat(v.toFixed(1))
                  }
                })}
                min={1.0}
                max={3.0}
                step={0.1}
                description="1.0 = linear. 2.0 = steep drop-off for below-average players"
              />
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 3: Anchor Player Settings */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <Target className="inline w-5 h-5 mr-2" />
            Anchor Player Settings
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
            Top players used to establish the pricing curve
          </p>

          <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
            <Slider
              label="Anchor Multiplier"
              value={pricingLogic.price_constraints.max_players_to_price_multiplier}
              onChange={(v) => setPricingLogic({
                ...pricingLogic,
                price_constraints: {
                  ...pricingLogic.price_constraints,
                  max_players_to_price_multiplier: v
                }
              })}
              min={1.0}
              max={2.0}
              step={0.25}
              suffix="×"
            />

            {leagueStats && (
              <div className={`mt-4 p-3 rounded-md ${dynastyTheme.classes.bg.dark} border ${dynastyTheme.classes.border.neutral}`}>
                <p className={dynastyTheme.classes.text.white}>
                  <span className="font-semibold">Formula:</span>{' '}
                  Teams ({leagueStats.numTeams}) × Roster ({leagueStats.rosterSize}) × Multiplier ({pricingLogic.price_constraints.max_players_to_price_multiplier.toFixed(2)}) = {' '}
                  <span className={dynastyTheme.classes.text.primary + ' font-bold'}>
                    {leagueStats.anchorPlayerCount} anchor players
                  </span>
                </p>
                <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-2`}>
                  Top {leagueStats.anchorPercent}% of players get adaptive pricing.
                  Remaining {leagueStats.totalPlayers - leagueStats.anchorPlayerCount} players priced using linear model.
                </p>
              </div>
            )}
          </div>

          <HelpText title="What are anchor players?">
            <p className="mb-2">
              Anchor players are the elite performers who establish the pricing curve.
              Their prices are calculated using adaptive distribution (exponential curve).
              All other players are priced linearly based on this established curve.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="font-semibold text-green-400 mb-1">Larger multiplier (1.75-2.0×):</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>More players get premium pricing</li>
                  <li>Less distinction between elite and good</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-red-400 mb-1">Smaller multiplier (1.0-1.25×):</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Clear elite tier (only top 10-15%)</li>
                  <li>May underprice above-average players</li>
                </ul>
              </div>
            </div>
            <p className="mt-3 text-yellow-400">
              Recommendation: 1.5× for balanced pricing
            </p>
          </HelpText>
        </div>
      </div>

      {/* SECTION 4: Price Constraints */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <DollarSign className="inline w-5 h-5 mr-2" />
            Price Constraints
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
            Set pricing boundaries and distribution targets
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Draft Cap Usage Target"
                value={Math.round(pricingLogic.price_constraints.draft_cap_usage_target * 100)}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  price_constraints: {
                    ...pricingLogic.price_constraints,
                    draft_cap_usage_target: v / 100
                  }
                })}
                min={50}
                max={100}
                step={5}
                suffix="%"
              />

              <div className={`mt-3 p-3 rounded-md ${dynastyTheme.classes.bg.dark} border ${dynastyTheme.classes.border.neutral}`}>
                <p className={dynastyTheme.classes.text.white + ' text-sm'}>
                  Draft cap: <span className={dynastyTheme.classes.text.primary}>${draftCap}</span> × {Math.round(pricingLogic.price_constraints.draft_cap_usage_target * 100)}% = {' '}
                  <span className={dynastyTheme.classes.text.primary + ' font-bold'}>${calculatedValues.perTeamTarget}</span> per-team target
                </p>
                <p className={dynastyTheme.classes.text.neutralLight + ' text-sm mt-1'}>
                  Max single player: 10% of ${calculatedValues.perTeamTarget} = {' '}
                  <span className={dynastyTheme.classes.text.primary + ' font-bold'}>${calculatedValues.maxPlayerPrice}</span>
                </p>
              </div>
            </div>

            <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
              <Slider
                label="Minimum Value Players"
                value={Math.round(pricingLogic.price_constraints.min_value_players_percent * 100)}
                onChange={(v) => setPricingLogic({
                  ...pricingLogic,
                  price_constraints: {
                    ...pricingLogic.price_constraints,
                    min_value_players_percent: v / 100
                  }
                })}
                min={5}
                max={30}
                step={5}
                suffix="%"
              />

              <div className={`mt-3 p-3 rounded-md ${dynastyTheme.classes.bg.dark} border ${dynastyTheme.classes.border.neutral}`}>
                <p className={dynastyTheme.classes.text.white + ' text-sm'}>
                  Bottom {Math.round(pricingLogic.price_constraints.min_value_players_percent * 100)}% of players ({calculatedValues.minValuePlayersCount} players) priced at minimum salary (${calculatedValues.minSalary})
                </p>
              </div>
            </div>
          </div>

          {/* Below-Median Price Curve - Controls how steeply prices drop below anchor */}
          <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md mt-6`}>
            <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-3`}>
              Below-Median Price Curve
            </label>
            <div className="flex items-center gap-4">
              {[
                { value: 1, label: 'Linear', description: 'Gradual descent (original)' },
                { value: 2, label: 'Steep', description: 'Recommended - faster drop' },
                { value: 3, label: 'Very Steep', description: 'Aggressive price drop' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setPricingLogic({
                    ...pricingLogic,
                    price_constraints: {
                      ...pricingLogic.price_constraints,
                      below_median_curve_power: option.value
                    }
                  })}
                  className={`flex-1 p-3 rounded-md border-2 ${dynastyTheme.classes.transition} ${
                    (pricingLogic.price_constraints.below_median_curve_power ?? 2) === option.value
                      ? `${dynastyTheme.classes.border.primary} ${dynastyTheme.classes.bg.primaryLight}`
                      : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                  }`}
                >
                  <div className={`font-bold ${
                    (pricingLogic.price_constraints.below_median_curve_power ?? 2) === option.value
                      ? dynastyTheme.classes.text.primary
                      : dynastyTheme.classes.text.white
                  }`}>
                    {option.label}
                  </div>
                  <div className={`text-xs mt-1 ${dynastyTheme.classes.text.neutralLight}`}>
                    {option.description}
                  </div>
                </button>
              ))}
            </div>

            <div className={`mt-3 p-3 rounded-md ${dynastyTheme.classes.bg.dark} border ${dynastyTheme.classes.border.neutral}`}>
              <p className={dynastyTheme.classes.text.white + ' text-sm'}>
                <span className="font-semibold">Effect:</span> Controls how quickly prices drop for players below the median anchor price.
                "Steep" (power=2) means a player at 75% position gets 56% of anchor price instead of 75%.
              </p>
            </div>
          </div>

          <HelpText title="How do price constraints work?">
            <p className="mb-3">
              <span className="font-semibold text-yellow-400">Draft Cap Usage Target:</span> Controls how much of the draft cap teams are expected to use.
            </p>
            <p className="mb-2">Why not 100%?</p>
            <ul className="list-disc list-inside space-y-1 mb-3">
              <li>Need flexibility for mid-season acquisitions</li>
              <li>Avoid forcing teams to spend on bad players</li>
              <li>Common setting: 70-80%</li>
            </ul>
            <p className="mb-3">
              <span className="font-semibold text-yellow-400">Minimum Value Players %:</span> Controls how many players price at minimum salary.
            </p>
            <p className="text-yellow-400">
              Recommendation: 15% (roughly bottom quintile)
            </p>
          </HelpText>
        </div>
      </div>

      {/* SECTION 5: Position Scarcity */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <Users className="inline w-5 h-5 mr-2" />
            Position Scarcity Multipliers
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
            Boost impact scores for scarce positions
          </p>

          {/* Position grid preview */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
            {Object.entries(pricingLogic.position_scarcity).map(([pos, multiplier]) => (
              <div
                key={pos}
                className={`${dynastyTheme.classes.bg.darkLighter} p-3 rounded-md text-center ${
                  multiplier > 1.0 ? 'border border-yellow-400/50' : ''
                }`}
              >
                <div className={dynastyTheme.classes.text.white + ' font-bold'}>{pos}</div>
                <div className={`text-sm ${multiplier > 1.0 ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight}`}>
                  {multiplier.toFixed(2)}×
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => setPositionScarcityModalOpen(true)}
            className={dynastyTheme.utils.getComponent('button', 'secondary', 'md') + ' w-full'}
          >
            <Settings className="w-4 h-4 mr-2" />
            Edit Multipliers
          </button>

          <div className={`mt-4 p-3 rounded-md ${dynastyTheme.classes.bg.darkLighter}`}>
            <p className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>
              <Info className="inline w-4 h-4 mr-1" />
              These multipliers apply AFTER z-score calculation. Example: Bobby Witt (SS) impact 8.77 × 1.05 = 9.21
            </p>
          </div>
        </div>
      </div>

      {/* SECTION 6: Advanced Settings (Collapsible) */}
      <div className={dynastyTheme.components.card.base}>
        <button
          onClick={() => setAdvancedSettingsOpen(!advancedSettingsOpen)}
          className="w-full p-6 flex items-center justify-between"
        >
          <h3 className={dynastyTheme.components.heading.h3 + ' mb-0'}>
            <Settings className="inline w-5 h-5 mr-2" />
            Advanced Settings
          </h3>
          {advancedSettingsOpen ? (
            <ChevronUp className={`w-5 h-5 ${dynastyTheme.classes.text.neutralLight}`} />
          ) : (
            <ChevronDown className={`w-5 h-5 ${dynastyTheme.classes.text.neutralLight}`} />
          )}
        </button>

        {advancedSettingsOpen && (
          <div className="px-6 pb-6">
            <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
              Historical data blending settings
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
                <Slider
                  label="Prior Year Discount"
                  value={pricingLogic.prior_year_discount}
                  onChange={(v) => setPricingLogic({
                    ...pricingLogic,
                    prior_year_discount: v
                  })}
                  min={0.70}
                  max={1.0}
                  step={0.05}
                  description={`2023 stats weighted at ${Math.round(pricingLogic.prior_year_discount * 100)}% of face value`}
                />
              </div>

              <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md`}>
                <Slider
                  label="Two Years Ago Discount"
                  value={pricingLogic.two_year_discount}
                  onChange={(v) => setPricingLogic({
                    ...pricingLogic,
                    two_year_discount: v
                  })}
                  min={0.50}
                  max={0.90}
                  step={0.05}
                  description={`2022 stats weighted at ${Math.round(pricingLogic.two_year_discount * 100)}% of face value`}
                />
              </div>
            </div>

            <div className={`mt-4 p-3 rounded-md ${dynastyTheme.classes.bg.warning} ${dynastyTheme.classes.text.white} text-sm`}>
              <AlertCircle className="inline w-4 h-4 mr-2" />
              Only change these if you understand historical blending logic
            </div>

            <HelpText title="When are these used?">
              <p className="mb-2">
                When blending current stats with historical data for players with small current-year samples (injuries, callups, etc.).
              </p>
              <p className="mb-2">
                <span className="font-semibold">Example: Ronald Acuña (192 ABs in 2024, injured)</span>
              </p>
              <ul className="list-disc list-inside space-y-1 mb-3">
                <li>2024 stats: 192 ABs (weight: 64%)</li>
                <li>2023 stats: 157 games, 41 HR, 73 SB (weight: 36% × 0.85 discount)</li>
                <li>Blended result used for pricing</li>
              </ul>
              <p className="text-yellow-400">
                Recommendation: Leave at defaults unless you have specific reason to change
              </p>
            </HelpText>
          </div>
        )}
      </div>

      {/* Scenario Management */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <h3 className={dynastyTheme.components.heading.h3}>
            <Copy className="inline w-5 h-5 mr-2" />
            Save & Compare Scenarios
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mb-4`}>
            Save current configuration to test and compare different pricing strategies
          </p>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="Scenario name..."
              className={`flex-1 ${dynastyTheme.components.input}`}
            />
            <button
              onClick={handleSaveScenario}
              disabled={!scenarioName.trim() || savingScenario}
              className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
            >
              <Plus className="w-4 h-4 mr-2" />
              {savingScenario ? 'Saving...' : 'Save Scenario'}
            </button>
          </div>

          {savedScenarios.length > 0 && (
            <div className="space-y-2">
              <h4 className={`${dynastyTheme.classes.text.white} font-semibold mb-2`}>
                Saved Scenarios
              </h4>
              {savedScenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className={`${dynastyTheme.classes.bg.darkLighter} p-3 rounded-md flex items-center justify-between`}
                >
                  <div>
                    <span className={`${dynastyTheme.classes.text.white} font-medium`}>
                      {scenario.name}
                    </span>
                    <span className={`${dynastyTheme.classes.text.neutralLight} text-xs ml-2`}>
                      {new Date(scenario.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleLoadScenario(scenario)}
                      className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
                    >
                      Load
                    </button>
                    <button
                      onClick={() => handleDeleteScenario(scenario.id)}
                      className={dynastyTheme.utils.getComponent('button', 'danger', 'sm')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Real-Time Preview */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={dynastyTheme.components.heading.h3}>
                <TrendingUp className="inline w-5 h-5 mr-2" />
                Real-Time Preview
              </h3>
              <p className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
                See how your settings affect player prices
              </p>
            </div>
            <button
              onClick={handleGeneratePreview}
              disabled={generatingPreview || !dataLoaded}
              className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
            >
              {generatingPreview ? 'Generating...' : 'Generate Preview'}
            </button>
          </div>

          {previewData && (
            <div className="space-y-6 mt-6">
              {/* Summary Stats */}
              {previewData.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md text-center`}>
                    <div className={`${dynastyTheme.classes.text.primary} text-3xl font-bold mb-1`}>
                      ${previewData.summary.maxSalary}
                    </div>
                    <div className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>Max Salary</div>
                  </div>
                  <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md text-center`}>
                    <div className={`${dynastyTheme.classes.text.primary} text-3xl font-bold mb-1`}>
                      ${previewData.summary.avgSalary}
                    </div>
                    <div className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>Avg Salary</div>
                  </div>
                  <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md text-center`}>
                    <div className={`${dynastyTheme.classes.text.primary} text-3xl font-bold mb-1`}>
                      ${previewData.summary.avgTeamSpend}
                    </div>
                    <div className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>Avg Team Spend</div>
                  </div>
                  <div className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-md text-center`}>
                    <div className={`${dynastyTheme.classes.text.primary} text-3xl font-bold mb-1`}>
                      {previewData.summary.capUsagePercent}%
                    </div>
                    <div className={dynastyTheme.classes.text.neutralLight + ' text-sm'}>Cap Usage</div>
                  </div>
                </div>
              )}

              {/* Sample Players */}
              <div>
                <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-3`}>
                  Elite Tier (Top 5)
                </h4>
                <DynastyTable
                  data={previewData.elite}
                  columns={[
                    { key: 'player_name', title: 'Player', width: 150 },
                    { key: 'position', title: 'Pos', width: 60 },
                    { key: 'team', title: 'Team', width: 60 },
                    { key: 'price', title: 'Price', width: 80, render: (value) => value ? `$${value}` : '$0' },
                    { key: 'tier', title: 'Tier', width: 80 },
                    { key: 'aggregate_z_score', title: 'Impact Score', width: 120, render: (value) => typeof value === 'number' ? value.toFixed(2) : (value || '0.00') }  /* String `||`: post-typeof-check fallback for non-numeric (null/undefined/empty string) → "0.00" display. */
                  ]}
                  tableType="flat-data"
                  maxHeight="300px"
                />
              </div>

              <div>
                <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-3`}>
                  Mid Tier (Sample)
                </h4>
                <DynastyTable
                  data={previewData.mid}
                  columns={[
                    { key: 'player_name', title: 'Player', width: 150 },
                    { key: 'position', title: 'Pos', width: 60 },
                    { key: 'team', title: 'Team', width: 60 },
                    { key: 'price', title: 'Price', width: 80, render: (value) => value ? `$${value}` : '$0' },
                    { key: 'tier', title: 'Tier', width: 80 },
                    { key: 'aggregate_z_score', title: 'Impact Score', width: 120, render: (value) => typeof value === 'number' ? value.toFixed(2) : (value || '0.00') }  /* String `||`: post-typeof-check fallback for non-numeric (null/undefined/empty string) → "0.00" display. */
                  ]}
                  tableType="flat-data"
                  maxHeight="300px"
                />
              </div>

              <div>
                <h4 className={`${dynastyTheme.classes.text.primary} font-semibold mb-3`}>
                  Replacement Tier (Sample)
                </h4>
                <DynastyTable
                  data={previewData.replacement}
                  columns={[
                    { key: 'player_name', title: 'Player', width: 150 },
                    { key: 'position', title: 'Pos', width: 60 },
                    { key: 'team', title: 'Team', width: 60 },
                    { key: 'price', title: 'Price', width: 80, render: (value) => value ? `$${value}` : '$0' },
                    { key: 'tier', title: 'Tier', width: 80 },
                    { key: 'aggregate_z_score', title: 'Impact Score', width: 120, render: (value) => typeof value === 'number' ? value.toFixed(2) : (value || '0.00') }  /* String `||`: post-typeof-check fallback for non-numeric (null/undefined/empty string) → "0.00" display. */
                  ]}
                  tableType="flat-data"
                  maxHeight="300px"
                />
              </div>
            </div>
          )}

          {!dataLoaded && (
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} text-center`}>
              <AlertCircle className={`w-6 h-6 mx-auto mb-2 ${dynastyTheme.classes.text.neutralLight}`} />
              <p className={dynastyTheme.classes.text.neutralLight}>
                Player data is loading... Preview will be available once data is ready.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Position Scarcity Modal */}
      <PositionScarcityModal
        isOpen={positionScarcityModalOpen}
        onClose={() => setPositionScarcityModalOpen(false)}
        positionScarcity={pricingLogic.position_scarcity}
        onChange={(newScarcity) => setPricingLogic({
          ...pricingLogic,
          position_scarcity: newScarcity
        })}
      />
    </div>
  );
};

export default PricingLogicTab;
